/**
 * Planning mode store using Apollo reactive variables.
 *
 * Tracks in-memory draft state for scenario creation and editing.
 * Draft actions are ephemeral — they only persist until Save or Discard.
 *
 * Planning mode is active when a user:
 * - Clicks "Create Scenario" (creating new)
 * - Makes a change to an existing saved scenario (editing existing)
 * @module features/squad-planning/stores/planningModeStore
 */

import { makeVar, useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  TransitionType,
  type ScenarioActionInput as GqlScenarioActionInput,
} from "@/generated/graphql";

import type { ActionDetails, PlayerAction } from "../types";
import { transitionsOnlyVar } from "./mobileToolbarStore";
import type { SignedPlayerData } from "./scenarioStore";

/** Maps frontend PlayerAction to backend TransitionType. */
const ACTION_TO_TRANSITION: Record<PlayerAction, TransitionType> = {
  Sell: TransitionType.Sell,
  Loan: TransitionType.LoanOut,
  Promote: TransitionType.Promote,
  Sign: TransitionType.Sign,
};

/** Snapshot of scenario state before editing (for discard). */
export interface ScenarioSnapshot {
  readonly actions: Readonly<Record<string, PlayerAction>>;
  readonly details: Readonly<Record<string, ActionDetails>>;
  readonly signedPlayers: Readonly<Record<string, SignedPlayerData>>;
}

/**
 * What one save submitted: the scenario being edited and the draft as sent.
 * A save settles against this, never against the draft that exists when the
 * mutation resolves (SE-8347) — edits drafted meanwhile stay unsaved.
 */
export interface DraftSubmission {
  readonly scenarioId: string | null;
  readonly snapshot: ScenarioSnapshot;
}

/** Full planning mode state. */
interface PlanningModeState {
  readonly isActive: boolean;
  /** null = creating new scenario, string = editing existing scenario ID */
  readonly editingScenarioId: string | null;
  readonly originalSnapshot: ScenarioSnapshot | null;
  readonly draftActions: Readonly<Record<string, PlayerAction>>;
  readonly draftDetails: Readonly<Record<string, ActionDetails>>;
  readonly draftSignedPlayers: Readonly<Record<string, SignedPlayerData>>;
  /** Details preserved when an action is cancelled, for re-promote pre-fill */
  readonly removedDetails: Readonly<Record<string, ActionDetails>>;
}

const INITIAL_STATE: PlanningModeState = {
  isActive: false,
  editingScenarioId: null,
  originalSnapshot: null,
  draftActions: {},
  draftDetails: {},
  draftSignedPlayers: {},
  removedDetails: {},
};

const planningModeVar = makeVar<PlanningModeState>(INITIAL_STATE);

/**
 * The player id "Model this sale" asked Squad Planning to open a Sell action
 * for, or null once consumed (SE-8786).
 *
 * A one-shot SIGNAL, not a second home for planning-mode truth: it exists
 * only so `ActionMenuContainer` — mounted once per player row, with no
 * connection to the route the reader arrived from — can know which row, if
 * any, should seed its Sell modal open. `useSellDeepLink` sets it
 * (after confirming the player is actually in the squad) and `ActionMenuContainer`
 * reads it ONCE, at mount, to seed `sellModalOpen`'s initial state — the same
 * "seed, don't sync" discipline `ObjectivesButtonContainer` documents for its
 * own one-shot deep-link params. It is cleared from the SAME handlers that
 * already close the modal (confirm, close, cancel) rather than proactively
 * on read, so a reader who has not yet acted on it is unaffected by an
 * unrelated re-render — mirroring that same container's choice to clear its
 * URL param on close rather than on mount.
 */
export const pendingSellDeepLinkVar = makeVar<string | null>(null);

/**
 * Converts a single draft action + details into the GQL mutation input format.
 *
 * When the draft carries NO details entry for an action, `financialDetails` is
 * omitted from the input entirely rather than sent as an all-nulls object. The
 * backend merges with `action.financialDetails ?? prev.financialDetails`, so a
 * present-but-empty object counts as present and clobbers the stored values;
 * omitting the key lets the fallback preserve them (SE-5567). An explicit
 * details entry whose fields the user cleared is still sent, so intentionally
 * blanking a value keeps working.
 *
 * Contract (SE-5596, `ScenarioActionInput` schema): `playerId` is required for
 * actions on existing players and OMITTED for external signs;
 * `externalPlayerData` is only for SIGN actions targeting players outside the
 * system. Pre-fix this attached `externalPlayerData` to every searched
 * signing — including the org's own roster/academy players — which the
 * backend filed as external-player rows (the SE-5564 invisible-sign bug;
 * backend read-path tolerance shipped in SE-5582 and stays as a legacy
 * shield, but the frontend must write the contract-correct shape).
 * @param playerId - System player ID; empty/absent only for external signs
 * @param action - Frontend action type
 * @param details - Financial details, or undefined for an untouched action
 * @param signedPlayer - Search-modal display metadata (kept for UI; only sent
 * as externalPlayerData when the player has no system playerId)
 * @returns GQL-formatted ScenarioActionInput
 */
function toGqlAction(
  playerId: string,
  action: PlayerAction,
  details: ActionDetails | undefined,
  signedPlayer?: SignedPlayerData
): GqlScenarioActionInput {
  // Spread, so the key is genuinely ABSENT (not merely `undefined`) for an
  // untouched action — see the doc comment above.
  const financialDetailsEntry =
    details == null
      ? {}
      : {
          financialDetails: {
            salePrice: details.salePrice ?? null,
            loanFee: details.loanFee ?? null,
            signingFee: details.transferFee ?? null,
            wage: details.annualSalary ?? null,
            durationMonths: details.durationMonths ?? null,
            signingBonus: details.signingBonus ?? null,
            agentFee: details.agentFee ?? null,
            coveredSharePercent: details.coveredSharePercent ?? null,
          },
        };

  if (signedPlayer != null && !playerId) {
    // True external sign (no system record): externalPlayerData INSTEAD of
    // playerId, per the input contract.
    return {
      transitionType: ACTION_TO_TRANSITION[action],
      ...financialDetailsEntry,
      externalPlayerData: {
        name: signedPlayer.displayName,
        teamName: signedPlayer.teamName,
        positions: signedPlayer.positions
          ? signedPlayer.positions.split(",").map(s => s.trim())
          : undefined,
        age: signedPlayer.age,
        gpr: signedPlayer.gprScore,
        nationalityName: signedPlayer.nationalityName,
      },
    };
  }

  return {
    playerId,
    transitionType: ACTION_TO_TRANSITION[action],
    ...financialDetailsEntry,
  };
}

/**
 * Every staged action as the backend's action input.
 *
 * Lifted out of `getDraftPayload` by SE-8458 so the two paths that need this
 * share ONE mapping. The save path reads the store imperatively and does not
 * care when the draft changes; the Live Impact verdict must recompute the
 * instant it does, so it passes the reactive slices straight in. A second copy
 * of this loop would let the deal the panel judges drift from the deal the user
 * saves — which is the class of defect SE-8458 exists to close.
 * @param draftActions - Which action is staged per player.
 * @param draftDetails - The terms recorded for each.
 * @param draftSignedPlayers - External-sign data, for players with no record.
 * @returns The staged actions, in the backend's input shape.
 */
export function buildDraftPayload(
  draftActions: Readonly<Record<string, PlayerAction>>,
  draftDetails: Readonly<Record<string, ActionDetails>>,
  draftSignedPlayers: Readonly<Record<string, SignedPlayerData>>
): readonly GqlScenarioActionInput[] {
  return Object.entries(draftActions).map(([playerId, action]) =>
    toGqlAction(
      playerId,
      action,
      draftDetails[playerId],
      draftSignedPlayers[playerId]
    )
  );
}

/**
 * Hook for managing planning mode state.
 * @returns Planning mode state and control methods
 */
export function usePlanningMode() {
  const state = useReactiveVar(planningModeVar);

  const draftActionCount = useMemo(() => {
    const originalActions = state.originalSnapshot?.actions ?? {};
    const originalDetails = state.originalSnapshot?.details ?? {};
    const draft = state.draftActions;
    const draftDets = state.draftDetails;

    const changed = Object.entries(draft).filter(([id, action]) => {
      if (originalActions[id] !== action) return true;
      // Action type matches — check if financial details differ
      const orig = originalDetails[id];
      const curr = draftDets[id];
      if (orig == null || curr == null) return orig !== curr;
      return JSON.stringify(orig) !== JSON.stringify(curr);
    }).length;
    const removed = Object.keys(originalActions).filter(
      id => !(id in draft)
    ).length;
    return changed + removed;
  }, [state.draftActions, state.draftDetails, state.originalSnapshot]);

  const enterCreateMode = useCallback(() => {
    const current = planningModeVar();
    // Re-entering create mode while a create-mode draft is already in flight is
    // a no-op, not a hard reset. In create mode there is no originalSnapshot,
    // so a non-empty `draftActions` is unsaved user work; blowing it away here
    // silently emptied the scenario the user went on to create (SE-5564).
    // Callers that genuinely want a fresh draft `discard()` first — see
    // ScenarioPillBarContainer's confirm-discard flow. Entering from a clean
    // state (or from edit mode) still resets, as before.
    if (
      current.isActive &&
      current.editingScenarioId == null &&
      Object.keys(current.draftActions).length > 0
    ) {
      return;
    }
    planningModeVar({
      isActive: true,
      editingScenarioId: null,
      originalSnapshot: null,
      draftActions: {},
      draftDetails: {},
      draftSignedPlayers: {},
      removedDetails: {},
    });
  }, []);

  const enterEditMode = useCallback(
    (
      scenarioId: string,
      existingActions: Readonly<Record<string, PlayerAction>>,
      existingDetails: Readonly<Record<string, ActionDetails>>,
      existingSignedPlayers: Readonly<Record<string, SignedPlayerData>>
    ) => {
      planningModeVar({
        isActive: true,
        editingScenarioId: scenarioId,
        originalSnapshot: {
          actions: existingActions,
          details: existingDetails,
          signedPlayers: existingSignedPlayers,
        },
        draftActions: { ...existingActions },
        draftDetails: { ...existingDetails },
        draftSignedPlayers: { ...existingSignedPlayers },
        removedDetails: {},
      });
    },
    []
  );

  const discard = useCallback(() => {
    planningModeVar(INITIAL_STATE);
  }, []);

  const exitPlanningMode = useCallback(() => {
    planningModeVar(INITIAL_STATE);
    // Reset transitions-only filter when exiting planning mode
    transitionsOnlyVar(false);
  }, []);

  /** After a successful save, promote the current draft to the new baseline. */
  const commitDraft = useCallback(() => {
    const current = planningModeVar();
    planningModeVar({
      ...current,
      originalSnapshot: {
        actions: { ...current.draftActions },
        details: { ...current.draftDetails },
        signedPlayers: { ...current.draftSignedPlayers },
      },
    });
  }, []);

  const addDraftAction = useCallback(
    (playerId: string, action: PlayerAction, details: ActionDetails) => {
      const current = planningModeVar();
      planningModeVar({
        ...current,
        draftActions: { ...current.draftActions, [playerId]: action },
        draftDetails: { ...current.draftDetails, [playerId]: details },
      });
    },
    []
  );

  const removeDraftAction = useCallback((playerId: string) => {
    const current = planningModeVar();
    const { [playerId]: _a, ...remainingActions } = current.draftActions;
    const { [playerId]: removedDetail, ...remainingDetails } =
      current.draftDetails;
    const { [playerId]: _sp, ...remainingSignedPlayers } =
      current.draftSignedPlayers;
    planningModeVar({
      ...current,
      draftActions: remainingActions,
      draftDetails: remainingDetails,
      draftSignedPlayers: remainingSignedPlayers,
      removedDetails:
        removedDetail != null
          ? { ...current.removedDetails, [playerId]: removedDetail }
          : current.removedDetails,
    });
  }, []);

  /** The draft as it is now, tagged with the scenario it belongs to. */
  const snapshotDraft = useCallback((): DraftSubmission => {
    const current = planningModeVar();
    return {
      scenarioId: current.editingScenarioId,
      snapshot: {
        actions: { ...current.draftActions },
        details: { ...current.draftDetails },
        signedPlayers: { ...current.draftSignedPlayers },
      },
    };
  }, []);

  /**
   * After a successful save, move the baseline to what that save SUBMITTED,
   * keeping later edits unsaved. A no-op once the draft belongs to another
   * scenario or planning mode has ended.
   * @param submission - What the save sent
   * @returns Whether the baseline moved
   */
  const commitSubmission = useCallback((submission: DraftSubmission) => {
    const current = planningModeVar();
    if (
      !current.isActive ||
      current.editingScenarioId !== submission.scenarioId
    ) {
      return false;
    }
    planningModeVar({ ...current, originalSnapshot: submission.snapshot });
    return true;
  }, []);

  /**
   * After a refused save, drop one player's action — but only while the draft
   * still holds exactly what was submitted for that player, so a newer edit
   * or another scenario's draft is never rolled back by a stale failure.
   * @param submission - What the save sent
   * @param playerId - The player whose action the save carried
   * @returns Whether the action was removed
   */
  const rollBackSubmission = useCallback(
    (submission: DraftSubmission, playerId: string) => {
      const current = planningModeVar();
      const { actions, details } = submission.snapshot;
      const unchanged =
        current.isActive &&
        current.editingScenarioId === submission.scenarioId &&
        playerId in actions &&
        current.draftActions[playerId] === actions[playerId] &&
        current.draftDetails[playerId] === details[playerId];
      if (!unchanged) return false;
      removeDraftAction(playerId);
      return true;
    },
    [removeDraftAction]
  );

  /**
   * After the backend deleted one player's action (SE-8353), drop it from
   * BOTH the draft and the baseline: it is neither an unsaved removal nor
   * something a later full-set `updateScenario` may replay. Like
   * `commitSubmission`, it settles against the scenario the removal was SENT
   * for: a no-op (false) once the store edits another scenario or planning
   * mode has ended, so a completion that lands after a switch never touches
   * the new scenario's draft.
   * @param scenarioId - The scenario the removal was sent for
   * @param playerId - The player whose action the server no longer holds
   * @returns Whether the draft and baseline were changed
   */
  const dropPersistedAction = useCallback(
    (scenarioId: string, playerId: string) => {
      const current = planningModeVar();
      if (!current.isActive || current.editingScenarioId !== scenarioId) {
        return false;
      }
      const without = <T>(map: Readonly<Record<string, T>>) => {
        const { [playerId]: _dropped, ...rest } = map;
        return rest;
      };
      const baseline = current.originalSnapshot;
      planningModeVar({
        ...current,
        draftActions: without(current.draftActions),
        draftDetails: without(current.draftDetails),
        draftSignedPlayers: without(current.draftSignedPlayers),
        originalSnapshot:
          baseline == null
            ? null
            : {
                actions: without(baseline.actions),
                details: without(baseline.details),
                signedPlayers: without(baseline.signedPlayers),
              },
      });
      return true;
    },
    []
  );

  const addDraftSignedPlayer = useCallback(
    (playerId: string, data: SignedPlayerData) => {
      const current = planningModeVar();
      planningModeVar({
        ...current,
        draftSignedPlayers: {
          ...current.draftSignedPlayers,
          [playerId]: data,
        },
      });
    },
    []
  );

  const getDraftPayload = useCallback((): readonly GqlScenarioActionInput[] => {
    const current = planningModeVar();
    return buildDraftPayload(
      current.draftActions,
      current.draftDetails,
      current.draftSignedPlayers
    );
  }, []);

  return {
    isActive: state.isActive,
    isEditing: state.editingScenarioId != null,
    editingScenarioId: state.editingScenarioId,
    draftActionCount,
    draftActions: state.draftActions,
    draftDetails: state.draftDetails,
    draftSignedPlayers: state.draftSignedPlayers,
    originalActions: state.originalSnapshot?.actions ?? {},
    originalDetails: state.originalSnapshot?.details ?? {},
    removedDetails: state.removedDetails,
    enterCreateMode,
    enterEditMode,
    discard,
    exitPlanningMode,
    commitDraft,
    snapshotDraft,
    commitSubmission,
    rollBackSubmission,
    addDraftAction,
    removeDraftAction,
    dropPersistedAction,
    addDraftSignedPlayer,
    getDraftPayload,
  };
}
